import { call, select, put } from "redux-saga/effects";
import moment from "moment";
import { isNumber } from '@bitComponents/utils/common';
import { sendToSE as sendToSESaga } from "../bitSagas/sendToSE";
import { actions as uiNeedsActions } from "../reducers/uiNeeds";
import { commonActions } from "../reducers/common";
import { actions as taggingActions } from '../reducers/tagging';
import {
  generateSEMessage,
  generateImpersonationMessage,
  generateMobileInsuranceMessage,
  generateExtendedSEMessage,
} from "./normalizers/se";
import { STRING_KEYS } from "../localizedStrings/index";
import { getPaymentMethodNormalized } from "../utils/payment";
import { SE_PAYMENT_MODALITY } from "../bitConstants/constants";
import { MANUAL_PRODUCT_TYPES_MAPPING, PRODUCT_TYPES } from "../utils/catalog";
import { CQ_ACTION_CODES, CQ_POTS } from "../catalogConstants";
import { normalizeSelectElementsTaggingChoices } from "../tagging";
import { nationalities } from "../containers/RegisterPersonalData/nationalities";
import FrontSonda from "@gcloud-npm/utils.monitoring.front-sonda";

const apisNeedsStore = (state) => state.apiNeeds;
const seStore = (state) => state.se;
const uiNeedsStore = (state) => state.uiNeeds;
const offerStore = (state) => state.offer;
const manualStore = (state) => state.manual;
const flowStore = (state) => state.flow;
const localizedStringsStore = (state) => state.localizedStrings;
const contextStore = state => state.context.data;

export function* genericSendToSEMessage(params) {
  const { error } = params;

  const {
    productOfferingName,
    selectElementsNeeds,
    ppipInfo,
    addressInfo,
    accountInfo,
    deviceInfo,
    phoneNumberOTP,
    poitSelected,
    poshtSelected,
    afterSaleProduct,
    allChoices
  } = yield select(apisNeedsStore);
  const { installationAddress, afterSaleParentProduct } = yield select(seStore);

  const { localizedStrings } = yield select(localizedStringsStore);
  const { multiSim } = yield select(manualStore);
  const { allPOsToBuy, productOffering, cisi, r2rType, r2rSubType, speedId, r2rInfo } = yield select(offerStore);
  const { pricesInfo } = yield select(uiNeedsStore);
  const { flowJson } = yield select(flowStore);
  const { flowName } = flowJson;

  const poInfo =
    pricesInfo?.recurringCharges?.products &&
    Array.isArray(pricesInfo.recurringCharges.products)
      ? pricesInfo.recurringCharges.products.find(
          (product) => product.productOffering.id === productOffering || product.productOffering.id === cisi
        )
      : null;
  const buyInfo = {
    productName: productOfferingName,
    productOffering,
    promotion: poInfo?.promotionId || "",
    promotionName: poInfo?.promotionName || "",
    price: isNumber(poInfo?.finalPrice) ? poInfo.finalPrice : "",
    priceRecurring : true,
    mainAction: {
      actionId: selectElementsNeeds?.actionId || "",
      phoneNumber: selectElementsNeeds?.phoneNumber || "",
      iccNumber: selectElementsNeeds?.iccNumber || "",
    },
    secondarySelection: selectElementsNeeds ? selectElementsNeeds.secondarySelection : undefined,
    flowName,
    r2rType,
    r2rSubType
  };

  if(afterSaleProduct && afterSaleProduct.name && (afterSaleProduct.cisi || afterSaleProduct.poId)){
    buyInfo.productName = afterSaleProduct.name;
    if(afterSaleProduct.cisi){
      buyInfo.productOffering = afterSaleProduct.cisi;
    }
    if(pricesInfo &&
      pricesInfo.oneTimeCharges &&
      pricesInfo.oneTimeCharges.products &&
      Array.isArray(pricesInfo.oneTimeCharges.products)){
        const afterSaleProductPricesInfo = pricesInfo.oneTimeCharges.products[0]
        buyInfo.promotion = poInfo && poInfo.promotionId ? poInfo.promotionId : "";
        buyInfo.price = isNumber(afterSaleProductPricesInfo?.finalPrice) ? afterSaleProductPricesInfo.finalPrice : "";
        buyInfo.priceRecurring = false;
      }
  }

  let landLineSelections = null;
  if (allChoices?.[PRODUCT_TYPES.BAF]?.[0]?.secondaryChoices?.[PRODUCT_TYPES.LAND_LINE]) {
    landLineSelections = allChoices[PRODUCT_TYPES.BAF][0].secondaryChoices[PRODUCT_TYPES.LAND_LINE];
  }

  const shipping = {
    address: addressInfo && addressInfo.address ? addressInfo.address : "",
    type: poshtSelected || undefined,
  };

  let installation = {
    address:
      installationAddress && installationAddress.address
        ? installationAddress.address
        : "",
    type: poitSelected || undefined,
  };

  if(afterSaleParentProduct?.address){
    installation.address = afterSaleParentProduct.address;
  }
  let selectedMobileLine;
  if(afterSaleParentProduct?.phoneNumber){
    selectedMobileLine = afterSaleParentProduct.phoneNumber;
  }

  let ppip;
  if (ppipInfo && ppipInfo.id) {
    ppip = {
      selection: ppipInfo.ppipRequired,
    };
  }

  const payment = {
    accountNumber:
      accountInfo && accountInfo.customerAccountNumber
        ? accountInfo.customerAccountNumber
        : "",
    customerAccountId:
      accountInfo && accountInfo.customerAccountId
        ? accountInfo.customerAccountId
        : "",
  };

  let terminalInfo;

  if (deviceInfo && deviceInfo.name) {
    const { name, color, storage } = deviceInfo;

    terminalInfo = {
      mobileLine: phoneNumberOTP,
      productOfferingName: name,
      color,
      storage,
      productOffering,
    };
  }

  const message = generateSEMessage({
    error,
    buyInfo: flowJson.sendToSEWithoutBuyInfo ? null : buyInfo,
    shipping,
    payment,
    landLineSelections,
    installation,
    ppip,
    terminalInfo,
    cartInfo: pricesInfo,
    allPOsToBuy,
    localizedStrings,
    selectedMobileLine,
    speedId: speedId || "",
    multiSim,
    r2rInfo,
  });
  return message;
}

function* extendedSendToSEMessage(params) {
  const { error } = params;

  const {
    ppipInfo,
    addressInfo,
    accountInfo,
    poshtSelected,
    clientAddresses,
    allChoices
  } = yield select(apisNeedsStore);
  const { installationAddress } = yield select(seStore);
  const { speedId } = yield select(offerStore);

  const { localizedStrings } = yield select(localizedStringsStore);
  const { pricesInfo } = yield select(uiNeedsStore);

  let landLineSelections = [];
  if (
    allChoices &&
    allChoices[PRODUCT_TYPES.PACK] &&
    allChoices[PRODUCT_TYPES.PACK][0] &&
    allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices &&
    allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices[PRODUCT_TYPES.LAND_LINE]
  ) {
    landLineSelections = allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices[PRODUCT_TYPES.LAND_LINE];
  }

  let mobileLineSelections = [];
  if (allChoices &&
    allChoices[PRODUCT_TYPES.PACK] &&
    allChoices[PRODUCT_TYPES.PACK][0] &&
    allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices &&
    allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices[PRODUCT_TYPES.MOBILE]) {
    mobileLineSelections = allChoices[PRODUCT_TYPES.PACK][0].secondaryChoices[PRODUCT_TYPES.MOBILE];
  }

  const shipping = {
    address: addressInfo && addressInfo.address ? addressInfo.address : "",
    type: poshtSelected || undefined,
  };

  const installation = {
    address:
      installationAddress && installationAddress.address
        ? installationAddress.address
        : "",
  };

  if (installation && !installation.address) {
    const packAddress = clientAddresses.find(
      (address) => address.productType === PRODUCT_TYPES.PACK
    );
    if (packAddress) {
      installation.address = packAddress.fullAddress;
    }
  }

  let ppip;
  if (ppipInfo && ppipInfo.id) {
    ppip = {
      selection: ppipInfo.ppipRequired,
    };
  }

  const payment = {
    accountNumber:
      accountInfo && accountInfo.customerAccountNumber
        ? accountInfo.customerAccountNumber
        : "",
    customerAccountId:
      accountInfo && accountInfo.customerAccountId
        ? accountInfo.customerAccountId
        : "",
  };

  let bundleInfo = [];
  if (
    pricesInfo &&
    pricesInfo.recurringCharges &&
    pricesInfo.recurringCharges.products &&
    Array.isArray(pricesInfo.recurringCharges.products)
  ) {
    bundleInfo = pricesInfo.recurringCharges.products.filter(
      (product) => product.productType === PRODUCT_TYPES.PACK
    );
  }
  const message = generateExtendedSEMessage({
    error,
    bundleInfo,
    landLineSelections,
    mobileLineSelections,
    shipping,
    payment,
    installation,
    ppip,
    cartInfo: pricesInfo,
    speedId: speedId || "",
    localizedStrings,
  });
  return message;
}

function* manualSendToSEMessage(params) {
  const { error } = params;
  const {
    selectElementsNeeds,
    addressInfo,
    accountInfo,
    poitSelected,
    manual,
    register,
    deviceInfo,
  } = yield select(apisNeedsStore);
  const { installationAddress } = yield select(seStore); // OK
  const { productInfo, r2rRenewalInfo, multiSim } = yield select(manualStore); // OK
  const { localizedStrings } = yield select(localizedStringsStore);
  const { allPOsToBuy, productOffering } = yield select(offerStore);
  const { pricesInfo, secondaryOptionsObj, riskCode, monthlyCreditLimit, creditLimit } = yield select(uiNeedsStore);
  const { flowJson } = yield select(flowStore);
  const { flowName } = flowJson;
  const { secondaryOptionsSelected } = secondaryOptionsObj;
  const { inputs } = register;
  const { nationality } = inputs;

  const secondarySelection = secondaryOptionsSelected.map(selection => {
    return {
      productType: selection.productType,
      actionId: selection.actionId,
      description: `Línea nº ${selection.groupIndex+1}`,
      customerQuoteItems: [{
        clientPhoneNumberToBeAppliedTo: selection.phoneNumber,
        iccNumber: selection.iccNumber
      }]
    }
  });
  const selections = {
    actionId: selectElementsNeeds.actionId || CQ_ACTION_CODES.REGISTER,
    productType: selectElementsNeeds.productType || MANUAL_PRODUCT_TYPES_MAPPING[flowName],
    secondarySelection,
  }
  const selectElementsTaggingChoices = normalizeSelectElementsTaggingChoices(selections);
  yield put(taggingActions.setSelectElementsChoices(selectElementsTaggingChoices));

  const poInfo =
    pricesInfo &&
    pricesInfo.recurringCharges &&
    pricesInfo.recurringCharges.products &&
    Array.isArray(pricesInfo.recurringCharges.products)
      ? pricesInfo.recurringCharges.products.find(
          (product) => product.productOffering.id === productOffering
        )
      : null;
  let buyInfo = {
    productName: productInfo?.descripcion || "",
    productOffering,
    promotion: poInfo && poInfo.promotionId ? poInfo.promotionId : "",
    price: isNumber(poInfo?.finalPrice) ? poInfo.finalPrice : "",
    priceRecurring: true,
    mainAction: {
      actionId: selectElementsNeeds?.actionId || "",
      phoneNumber: selectElementsNeeds?.phoneNumber
        ? selectElementsNeeds?.phoneNumber
        : selectElementsNeeds?.clientPhoneNumberToBeAppliedTo || "",
      iccNumber: selectElementsNeeds?.iccNumber || "",
    },
    secondarySelection,
    flowName,
  };

  if(deviceInfo){
    buyInfo.isPrePurchase = deviceInfo.isPrePurchase;
    buyInfo.color = deviceInfo.color;
  }

  const shipping = {
    address: addressInfo?.address || "",
    type: addressInfo?.address ? CQ_POTS.SHIPPING_POT.HOME_SHIPPING_ID : '',
  };

  const installation = {
    address:
      installationAddress && installationAddress.address
        ? installationAddress.address
        : "",
    type: poitSelected || undefined,
  };

  const payment = {
    accountNumber:
      accountInfo && accountInfo.customerAccountNumber
        ? accountInfo.customerAccountNumber
        : "",
    customerAccountId:
      accountInfo && accountInfo.customerAccountId
        ? accountInfo.customerAccountId
        : "",
    sameAccount: localizedStrings[STRING_KEYS.se.message.payment.sameAccount],
  };

  const selectedMobileLine = manual.phoneNumber;
  
  let nationalityName = '';
  if( nationality ){
    const nation = nationalities.find(nationalityItem => nationalityItem.value === nationality);
    nationalityName = nation.label;
  }
  
  let r2rRenewal = null;

  if(r2rRenewalInfo){
    const { leasing, productName, productOfferingId, hasMobileInsurance } = r2rRenewalInfo;
    r2rRenewal = {
      cancellationFee: leasing.cancellationFee,
      productName,
      productOfferingId,
      hasMobileInsurance,
      riskCode,
      monthlyCreditLimit,
      creditLimit,
      pvpUntaxed: deviceInfo.pvpUntaxed
    }
  }

  const message = generateSEMessage({
    error,
    buyInfo,
    shipping,
    payment,
    installation,
    cartInfo: pricesInfo,
    allPOsToBuy,
    localizedStrings,
    forceShoppingCartSection: true,
    netflixProductToAdd: productInfo?.idPoN,
    additionalProductsToAdd: productInfo?.idPoTv,
    selectedMobileLine,
    nationalityName,
    r2rRenewal,
    multiSim,
  });
  return message;
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * actualizar/contacto (updateMyContacts).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* sendToSE(params) {
  const { showError, normalizeHttpErrors, error } = params;
  const reduxUpdate = {
    successUpdate: commonActions.setSEInfo,
    errorUpdate: showError,
  };

  const { clientWithoutAvailableCredit, clientWithDebt, clientWithUnknownRisk } = yield select(uiNeedsStore);

  if (clientWithDebt || clientWithUnknownRisk || clientWithoutAvailableCredit) {
    return null;
  }

  const { localizedStrings } = yield select(localizedStringsStore);
  const { personalContactInfo, cqId } = yield select(apisNeedsStore);
  const { flowJson } = yield select(flowStore);
  
  let message = "";
  let subject = localizedStrings[STRING_KEYS.se.order.subject];
  if (flowJson.extendedSEMessage) {
    message = yield call(extendedSendToSEMessage, { error });
  } else if (flowJson.manualSEMessage) {
    const { productTypes, productInfo } = yield select(manualStore);
    const { tipo } = productInfo;
    subject = productTypes[tipo].seSubject;
    message = yield call(manualSendToSEMessage, { error });
  } else {
    message = yield call(genericSendToSEMessage, { error });
  }
  const seParams = {
    contactPhone: personalContactInfo.contactPhone,
    contactEmail: personalContactInfo.email,
    cqId,
    message,
    subject,
    reduxUpdate,
    normalizeHttpErrors,
  };
  let response = null;
  try {
    response = yield call(sendToSESaga, seParams);
  } catch (e) {
    const context = yield select(contextStore);
    const frontSonda = new FrontSonda({
      processName: "sendToSE",
      context,
      filePath: "sagas/se",
    });
    frontSonda.fatalMessage({
      info: 'Error capturado en catch de sendToSE',
      error: e,
    });
    console.log(e);
  }

  return response;
}

export function* sendBackToSchoolToSE(params) {
  const { showError, normalizeHttpErrors } = params;
  const { localizedStrings } = yield select(localizedStringsStore);
  const reduxUpdate = {
    successUpdate: commonActions.setSEInfo,
    errorUpdate: showError,
  };

  const { personalContactInfo, cqId, backToSchoolMail } = yield select(apisNeedsStore);

  const message = `${localizedStrings[STRING_KEYS.backToSchool.SEMessage]}${backToSchoolMail}`

  const seParams = {
    contactPhone: personalContactInfo.contactPhone,
    contactEmail: personalContactInfo.email,
    cqId,
    message,
    subject: localizedStrings[STRING_KEYS.backToSchool.SESubject],
    reduxUpdate,
    normalizeHttpErrors,
  };
  let response = null;
  try {
    response = yield call(sendToSESaga, seParams);
  } catch (error) {
    console.log(error);
  }
}

export function* sendMobileInsuranceToSE(params) {
  const { showError, normalizeHttpErrors, terminalData } = params;
  const reduxUpdate = {
    successUpdate: uiNeedsActions.sendRiskToGenesysSuccess,
    errorUpdate: showError,
  };

  const {
    originalPermanentResidenceInfo,
    clientInfo,
    myProductsInfo,
    personalContactInfo,
    phoneNumberOTP,
    cqId,
  } = yield select(apisNeedsStore);
  const { localizedStrings } = yield select(localizedStringsStore);

  const { address } = originalPermanentResidenceInfo;
  const { docNumber, contactPhone } = clientInfo;
  const { linesMobile } = myProductsInfo;

  const mobileLineToSE =
    Array.isArray(linesMobile) &&
    linesMobile.length > 0 &&
    linesMobile[0].number;

  const {
    productOfferingName,
    color,
    storageId,
    selectedMobileInsuranceValue,
    productOffering,
    storage,
    poptSelected,
  } = terminalData;

  const subject =
    localizedStrings[STRING_KEYS.se.shippingMobileInsurance.subject];
  const terminalInfo = {
    mobileLine: phoneNumberOTP || mobileLineToSE,
    address,
    productOfferingName,
    color,
    storage,
    modality: SE_PAYMENT_MODALITY[getPaymentMethodNormalized(poptSelected)],
  };
  const tsInfo = {
    docNumber,
    mobileLine: phoneNumberOTP || mobileLineToSE,
    contactEmail: personalContactInfo.email,
    selectedMobileInsuranceValue,
    currentDateTime: moment().format("YYYYMMDDHHmm"),
    productOffering,
    storageId,
  };
  const message = generateMobileInsuranceMessage({
    terminalInfo,
    tsInfo,
  });
  const seParams = {
    contactPhone: contactPhone && contactPhone.value,
    contactEmail: personalContactInfo.email,
    message,
    subject,
    reduxUpdate,
    normalizeHttpErrors,
    cqId,
  };

  const response = yield call(sendToSESaga, seParams);

  return response;
}
